import { Badge } from "@/components/ui/badge";
import { ExternalLink, ArrowRight } from "lucide-react";
import { fmtMoney, tierLabel } from "./api";

export const PROPOSAL_STATUS = {
  ready: ["Lista para actualizar", "bg-emerald-100 text-emerald-800"],
  no_change: ["Sin cambios", "bg-slate-100 text-slate-600"],
  pending: ["Pendiente de revisión", "bg-amber-100 text-amber-800"],
  applying: ["Actualizando…", "bg-violet-100 text-violet-800"],
  applied: ["Actualizada en KimKim", "bg-emerald-200 text-emerald-900"],
  failed: ["Fallo al actualizar", "bg-red-100 text-red-800"],
};

export const PriceRows = ({ rows, testPrefix }) => (
  <table className="mt-2 w-full text-xs" data-testid={`${testPrefix}-table`}>
    <thead className="text-muted-foreground">
      <tr>
        <th className="text-left font-medium py-1">Tramo (pax)</th>
        <th className="text-right font-medium">Precio actual KimKim</th>
        <th className="text-right font-medium">Neto proveedor</th>
        <th className="text-right font-medium">Precio nuevo (neto + 8 %)</th>
        <th className="text-left font-medium pl-3">Nota</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={`${r.min}-${r.max}`} className={`border-t ${r.changed ? "font-semibold" : ""} ${r.ignored ? "text-muted-foreground line-through decoration-muted-foreground/40" : ""}`} data-testid={`${testPrefix}-row-${r.min}-${r.max}`}>
          <td className="py-1">{tierLabel(r.min, r.max)}{r.in_tariff && ((r.tariff_max != null && r.tariff_max !== r.max) || (r.tariff_min != null && r.tariff_min !== r.min)) ? <span className="text-muted-foreground font-normal"> (tarifa: {tierLabel(r.tariff_min ?? r.min, r.tariff_max ?? r.max)})</span> : null}</td>
          <td className="text-right font-mono">{r.in_kimkim ? fmtMoney(r.current, "") : <span className={r.ignored ? "" : "text-amber-700"}>{r.ignored ? "sin tramo" : "no existe"}</span>}</td>
          <td className="text-right font-mono">{r.in_tariff ? fmtMoney(r.net, "") : <span className="text-amber-700">no está en tarifario</span>}</td>
          <td className={`text-right font-mono ${r.changed ? "text-emerald-700" : ""}`}>{r.in_tariff ? fmtMoney(r.computed, "") : "—"}</td>
          <td className="pl-3 text-muted-foreground font-normal no-underline">{r.ignored ? "no se aplica: KimKim no tiene este tramo" : ""}{r.season_avg ? "media de temporadas" : ""}{r.changed ? (r.season_avg ? " · " : "") + "cambia" : r.in_kimkim && r.in_tariff ? "igual" : ""}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const ReasonLists = ({ notes, reasons, testId }) => (
  <>
    {notes?.length > 0 && (
      <ul className="mt-2 text-xs text-sky-800 list-disc pl-5" data-testid={`${testId}-notes`}>
        {notes.map((n, i) => <li key={i}>{n}</li>)}
      </ul>
    )}
    {reasons?.length > 0 && (
      <ul className="mt-2 text-xs text-amber-800 list-disc pl-5" data-testid={`${testId}-reasons`}>
        {reasons.map((r, i) => <li key={i}>{r}</li>)}
      </ul>
    )}
  </>
);

export const OwnerBadge = ({ owner, testId }) =>
  owner === "kimkim" ? (
    <Badge className="rounded-md bg-red-100 text-red-800 hover:bg-red-100" data-testid={testId}>propiedad de Kimkim · no se actualiza</Badge>
  ) : (
    <Badge className="rounded-md bg-sky-50 text-sky-800 hover:bg-sky-50" data-testid={testId}>Espíritu Travel</Badge>
  );

export const ProposalCard = ({ p, selected, onToggle }) => {
  const [label, cls] = PROPOSAL_STATUS[p.status] || [p.status, "bg-slate-100"];
  const cur = p.currency || "EUR";
  return (
    <div className={`rounded-md border p-3 ${selected ? "border-primary bg-primary/5" : ""}`} data-testid={`proposal-${p.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {p.status === "ready" && (
          <input data-testid={`proposal-select-${p.id}`} type="checkbox" checked={!!selected} onChange={() => onToggle(p.id)} className="h-4 w-4" />
        )}
        <span className="text-sm font-semibold">{p.provider_name}</span>
        <ArrowRight className="h-3 w-3 text-muted-foreground" />
        <span className="text-sm font-semibold">{p.kimkim_title}</span>
        {p.kimkim_url && <a href={p.kimkim_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" data-testid={`proposal-link-${p.id}`}><ExternalLink className="h-3 w-3" /></a>}
        <Badge className={`rounded-md ${cls} hover:${cls.split(" ")[0]}`} data-testid={`proposal-status-${p.id}`}>{label}</Badge>
        {p.kimkim_owner === "kimkim" && <OwnerBadge owner="kimkim" testId={`proposal-owner-${p.id}`} />}
        <span className="text-xs text-muted-foreground">{p.kimkim_pricing_type === "per_person_price" ? "precio por persona" : "precio por grupo"} · {cur}</span>
      </div>
      <PriceRows rows={p.rows} testPrefix={`proposal-${p.id}`} />
      <ReasonLists notes={p.notes} reasons={p.reasons} testId={`proposal-${p.id}`} />
      {p.error && <p className="mt-2 text-xs text-red-700">{p.error}</p>}
    </div>
  );
};
